import { z } from 'zod';
import { idSchema } from './common';
import { EVENT_TYPES } from './inbox';
import { classifyIpAddress, isInternalHostname } from './monitoring';

/**
 * Kalendarz z tajnego adresu iCal (DECISIONS 2026-10-07 „Bez Google Workspace”: kalendarz zwykłego konta Google
 * czytamy przez „Tajny adres w formacie iCal”, bez OAuth). Integracja rodzaju `google_calendar`, dostawca `ics`.
 *
 *  - Adres iCal to SEKRET (daje pełny odczyt kalendarza): zapis wyłącznie zaszyfrowany w `integrations.secret_enc`
 *    (envelope, bez podpowiedzi końcówki), w DTO tylko „ustawiony”; w `config` sama nazwa serwera (`icalHost`).
 *  - Synchronizacja (worker `calendar.sync` co 10 min + „Synchronizuj teraz”): wydarzenia z okna −7…+60 dni →
 *    `meetings` (`external_ref` = `cal:<integracja>:<p|s>:<skrót UID + wystąpienia>`), idempotentnie.
 *  - Prywatność: spotkania z kalendarza OSOBISTEGO widzi tylko jego właściciel, dopóki nie udostępni wybranego
 *    spotkania zespołowi (`p` → `s` w `external_ref`) — jak skrzynka osobista (DECISIONS 6.10).
 */

/** Dostawca rodzaju `google_calendar`: tajny adres iCal (plik .ics przez HTTPS). */
export const ICS_PROVIDER = 'ics';

/** Kolejka workera: synchronizacja kalendarzy (co 10 min, fan-out po workspace'ach) i „Synchronizuj teraz”. */
export const CALENDAR_QUEUES = { sync: 'calendar.sync' } as const;
export const CALENDAR_SYNC_CRON = '*/10 * * * *';
/** Kolejne kliknięcia „Synchronizuj teraz” w tym oknie = jeden przebieg. */
export const CALENDAR_MANUAL_SYNC_WINDOW_SEC = 30;

/**
 * Okno synchronizacji (dni wstecz i do przodu od „teraz”): nowe spotkania tylko z tego okna. `trackDays` = jak daleko
 * w przód szukamy wydarzeń już zapisanych (spotkanie przeniesione poza okno dostaje nowy termin zamiast archiwum).
 * Spotkania sprzed okna zostają bez zmian.
 */
export const CALENDAR_SYNC_WINDOW = { pastDays: 7, futureDays: 60, trackDays: 400 } as const;

/**
 * Nasze zaproszenie zniknięte z kanału (DECISIONS 2026-10-10 [S] „Usunięte w Kalendarzu Google = archiwum”): archiwum
 * dopiero, gdy wydarzenia nie ma w pełnych przebiegach co najmniej tyle sekund (czyli w ≥ 2 przebiegach). Tajny adres
 * iCal bywa serwowany z cache o różnej świeżości, więc jedno pobranie bez wydarzenia to jeszcze nie usunięcie; ponowne
 * zobaczenie zeruje odliczanie. Odwołanie i odrzucenie (jawny sygnał w kanale) archiwizują od razu.
 */
export const CALENDAR_MISSING_GRACE_SEC = 3 * 3600;

/**
 * Limity pobrania i przetwarzania pliku .ics: rozmiar (także po rozpakowaniu), czas całego pobrania, przekierowania
 * (każde przez ochronę SSRF), wydarzenia w pliku i wystąpienia w oknie (powyżej — przebieg bez archiwizacji
 * zniknięć, bo lista jest niepełna). `parseMs`: najdłuższy czas przetwarzania pobranego pliku (parser działa
 * synchronicznie w procesie API przy „Testuj” i w workerze — dłużej = błąd „zbyt złożony”; przegląd Fali 9).
 */
export const ICS_LIMITS = {
  maxBytes: 5 * 1024 * 1024,
  timeoutMs: 20_000,
  maxRedirects: 3,
  maxEvents: 20_000,
  maxOccurrencesInWindow: 1_000,
  parseMs: 5_000,
} as const;

/** Jedyny port adresu iCal (HTTPS). Inny port = błąd formularza („Testuj” nie sprawdza dowolnych usług). */
export const ICS_ALLOWED_PORTS = [443] as const;

/** Serwer tajnych adresów iCal Kalendarza Google (podpowiedź w formularzu i opis integracji). */
export const GOOGLE_CALENDAR_ICAL_HOST = 'calendar.google.com';

export const ICAL_URL_MESSAGES = {
  required: 'Wklej tajny adres w formacie iCal',
  invalid: 'To nie jest adres internetowy. Wklej cały adres zaczynający się od https://',
  https: 'Adres musi zaczynać się od https:// (połączenie szyfrowane)',
  credentials: 'Adres nie może zawierać loginu ani hasła',
  port: 'Adres kalendarza może używać tylko standardowego portu HTTPS (443)',
  host: 'Adres musi wskazywać publiczny serwer (bez adresu IP i nazw sieci lokalnej)',
  tooLong: 'Adres jest za długi',
} as const;

/** `webcal://` (Apple, Outlook) = ten sam adres przez HTTPS; spacje na brzegach usuwane. */
export function normalizeIcalUrl(raw: string): string {
  const trimmed = raw.trim();
  return /^webcals?:\/\//i.test(trimmed) ? trimmed.replace(/^webcals?:\/\//i, 'https://') : trimmed;
}

/** Problem z adresem iCal (walidacja formularza i usługi) albo null. Adres z DNS sprawdza dodatkowo pobieranie. */
export function icalUrlProblem(raw: string): string | null {
  const value = normalizeIcalUrl(raw);
  if (!value) return ICAL_URL_MESSAGES.required;
  if (value.length > 2048) return ICAL_URL_MESSAGES.tooLong;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return ICAL_URL_MESSAGES.invalid;
  }
  if (url.protocol !== 'https:') return ICAL_URL_MESSAGES.https;
  if (url.username || url.password) return ICAL_URL_MESSAGES.credentials;
  if (url.port && !(ICS_ALLOWED_PORTS as readonly number[]).includes(Number(url.port))) return ICAL_URL_MESSAGES.port;
  const host = url.hostname.toLowerCase();
  if (host.startsWith('[') || classifyIpAddress(host) !== null) return ICAL_URL_MESSAGES.host;
  if (!/[a-z]/.test(host.split('.').at(-1) ?? '') || isInternalHostname(host)) return ICAL_URL_MESSAGES.host;
  return null;
}

/** Tajny adres iCal z formularza: `webcal://` → `https://`, tylko HTTPS na porcie 443 i publiczna nazwa serwera. */
export const icalUrlSchema = z
  .string({ required_error: ICAL_URL_MESSAGES.required, invalid_type_error: ICAL_URL_MESSAGES.required })
  .transform(normalizeIcalUrl)
  .superRefine((v, ctx) => {
    const problem = icalUrlProblem(v);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  });

/** Nazwa serwera adresu iCal (jawna część adresu: do opisu integracji). Ścieżka adresu jest sekretem. */
export function icalUrlHost(raw: string): string | null {
  try {
    return new URL(normalizeIcalUrl(raw)).hostname.toLowerCase();
  } catch {
    return null;
  }
}

// ── external_ref spotkania z kalendarza ─────────────────────────────────────

/** Prefiks `meetings.external_ref` spotkań z kalendarza. */
export const CALENDAR_REF_PREFIX = 'cal';

export interface CalendarExternalRef {
  integrationId: string;
  /** spotkanie z kalendarza osobistego udostępnione zespołowi */
  shared: boolean;
  /** skrót (sha-256) UID + wystąpienia — stały między synchronizacjami */
  key: string;
}

/** `cal:<integracja>:<p|s>:<klucz>` — `p` = prywatne (kalendarz osobisty), `s` = udostępnione zespołowi. */
export function calendarExternalRef(integrationId: string, key: string, shared: boolean): string {
  return `${CALENDAR_REF_PREFIX}:${integrationId}:${shared ? 's' : 'p'}:${key}`;
}

const REF_RE = /^cal:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):([ps]):([0-9a-f]{16,64})$/;

export function parseCalendarExternalRef(ref: string | null | undefined): CalendarExternalRef | null {
  const m = ref ? REF_RE.exec(ref) : null;
  if (!m) return null;
  return { integrationId: m[1] as string, shared: m[2] === 's', key: m[3] as string };
}

/** Oba warianty odnośnika (prywatny i udostępniony) — synchronizacja znajduje spotkanie niezależnie od udostępnienia. */
export function calendarExternalRefVariants(integrationId: string, key: string): [string, string] {
  return [calendarExternalRef(integrationId, key, false), calendarExternalRef(integrationId, key, true)];
}

/**
 * Pola spotkania z kalendarza ustawiane przez synchronizację (źródło prawdy = kalendarz). Edycja w aplikacji tylko:
 * klient, projekt, notatki i transkrypcja — zmiana tytułu, terminu, miejsca albo uczestników = VALIDATION_ERROR
 * z komunikatem „zmień w kalendarzu”.
 */
export const CALENDAR_OWNED_MEETING_FIELDS = ['title', 'date', 'start', 'end', 'location', 'participants'] as const;
export type CalendarOwnedMeetingField = (typeof CALENDAR_OWNED_MEETING_FIELDS)[number];

/**
 * Spotkanie z kalendarza w widoku (`MeetingListItem.calendar`): kalendarz osobisty (prywatne, dopóki właściciel nie
 * udostępni) albo firmowy; `canShare` = właściciel kalendarza osobistego.
 */
export interface MeetingCalendarInfo {
  integrationId: string;
  personal: boolean;
  mine: boolean;
  /** właściciel cudzego kalendarza osobistego (widzi go osoba, której udostępniono) */
  ownerName: string | null;
  shared: boolean;
  canShare: boolean;
}

// ── Wejścia usług ───────────────────────────────────────────────────────────

/** „Synchronizuj teraz” jednego kalendarza (integracja). */
export const syncCalendarNowInput = z.object({ id: idSchema }).strict();
export interface SyncCalendarNowResult {
  queued: boolean;
}

/** Udostępnienie spotkania z kalendarza osobistego zespołowi (albo cofnięcie). Tylko właściciel kalendarza. */
export const shareCalendarMeetingInput = z
  .object({ id: idSchema, shared: z.boolean(), version: z.number().int().positive().optional() })
  .strict();

/** Wynik przebiegu synchronizacji (worker, testy). */
export interface CalendarSyncResult {
  integrationId: string | null;
  status: 'succeeded' | 'failed' | 'skipped';
  /** wystąpienia w oknie z pliku (bez całodniowych) */
  fetched: number;
  created: number;
  updated: number;
  /** odwołane w kalendarzu albo usunięte z okna → archiwum */
  archived: number;
  unchanged: number;
  /** wydarzenia całodniowe (pomijane) */
  allDay: number;
  /**
   * zaproszenia wysłane przez Fravity OS (UID `meeting-<id>@fravity-os`) do kalendarza osoby — spotkanie już jest
   * w aplikacji, więc pomijane (bez kopii)
   */
  ownInvites: number;
  /**
   * spotkania z Fravity OS usunięte (albo odrzucone / odwołane) w kalendarzu osoby po tym, jak kanał je pokazał
   * → archiwum bez odwołania wysyłanego z powrotem (DECISIONS 2026-10-10 [S] „Usunięte w Kalendarzu Google = archiwum”)
   */
  ownRemoved: number;
  /** więcej wystąpień niż limit — zapisano pierwsze, bez archiwizacji zniknięć */
  truncated: boolean;
  error: string | null;
  authLost: boolean;
}

/**
 * Spotkanie z Fravity OS usunięte w kalendarzu osoby (DECISIONS 2026-10-10 [S]): zdarzenie osobiste w Inboxie
 * właściciela kalendarza z „Przywróć spotkanie” (przywrócenie wysyła zaproszenie ponownie).
 */
export const CALENDAR_EVENT_TYPES = { meetingRemoved: EVENT_TYPES.meetingCalendarRemoved } as const;

/** Jak kalendarz osoby pokazał usunięcie naszego zaproszenia. */
export type CalendarRemovalKind = 'missing' | 'cancelled' | 'declined';

/** Powód w audycie archiwizacji (`meta.reason` — dziennik zdarzeń: „Powód”) i treść powiadomienia. */
export const CALENDAR_REMOVAL_TEXTS = {
  reasonLabel: 'usunięte w Kalendarzu Google',
  title: (meetingTitle: string, kind: CalendarRemovalKind) =>
    `Spotkanie „${meetingTitle}” zostało ${kind === 'declined' ? 'odrzucone' : 'usunięte'} w Kalendarzu Google — przeniesione do archiwum`,
  body: (kind: CalendarRemovalKind) =>
    [
      kind === 'declined'
        ? 'Zaproszenie odrzucono w Kalendarzu Google, więc Fravity OS przeniósł spotkanie do archiwum.'
        : 'Spotkanie zniknęło z Twojego Kalendarza Google, więc Fravity OS przeniósł je do archiwum.',
      'Jeśli to pomyłka, kliknij „Przywróć spotkanie” — wróci do listy, a kalendarz dostanie zaproszenie ponownie.',
    ].join('\n'),
  restore: 'Przywróć spotkanie',
  restored: 'Przywrócono spotkanie — zaproszenie wróci do kalendarza',
  alreadyActive: 'Spotkanie jest już przywrócone.',
} as const;

export const CALENDAR_MESSAGES = {
  fieldFromCalendar:
    'To pole pochodzi z kalendarza — zmień je w Kalendarzu Google. Tu ustawisz klienta, projekt, notatki i transkrypcję.',
  shareOnlyOwner: 'Udostępnić spotkanie może tylko właściciel kalendarza',
  shareOnlyPersonal: 'Spotkania z kalendarza firmowego widzi zespół (wg dostępu do klienta)',
  notCalendarMeeting: 'To spotkanie nie pochodzi z kalendarza',
  syncOnlyIcs: 'Synchronizacja działa dla kalendarza z tajnego adresu iCal',
  notConnected: 'Kalendarz nie jest podłączony',
  urlRequired: 'Wklej tajny adres w formacie iCal',
  urlOnlyIcs: 'Adres iCal podaje się tylko dla kalendarza z linku iCal',
  useIcalUrl: 'Kalendarz z linku iCal ma adres, nie klucz API — zmień go w ustawieniach kalendarza',
} as const;
